/**
 * 채널 편성표 계약 (v25, `docs/51-epg-design.md`).
 *
 * **시각이 없다.** 시작·종료 시각도, 격자도, `Schedule` 의 시각 컬럼도 없다 — 이 응답이
 * 주는 것은 **채널마다의 순서**이고, 시간을 말하는 것은 화면이며 그것은 **상대 경과**다
 * (`lineupStartOffsets`). 「시각 기반 편성표를 만들지 말 것」이 막는 것은 **모두가
 * 공유하는 기준점**이고, 여기에는 그것이 없다 (`docs/51` §0).
 *
 * ── ★ 이 응답으로는 아무것도 재생할 수 없다 ──────────────────
 *
 * `videoUrl`·`playlistId`·`deepLink`·`skip`·`cues` 가 **없는 것이 계약이다.**
 * 절대 규칙 33 은 스스로 「우회해도 화면은 똑같이 튼다 — 셋 다 기록에서만 드러난다」고
 * 적는데, **증상 없는 규칙은 사람이 지킬 수밖에 없다.** 그래서 논거의 형태를 바꾼다 —
 * **「하지 마라」가 아니라 「할 수 없다」**(절대 규칙 68).
 *
 * 필드를 더하고 싶어지면 `docs/51` §2.2 의 표를 먼저 읽을 것.
 *
 * ── 채널의 이름·썸네일·종류가 없는 것도 계약이다 ──────────────
 *
 * 그것은 `GET /v1/channels` 가 갖는다 — 저쪽은 **무인증이고 시청자별로 갈리지 않아**
 * 캐시할 수 있다(`docs/38` D10). 여기 실으면 앱이 그쪽을 버릴 수 있게 되고 **D10 이
 * 뒷문으로 뒤집힌다.** 화면은 목록을 먼저 그리고 이 응답을 그 위에 덧그린다 —
 * **EPG 가 실패해도 채널 목록은 그대로 뜬다.**
 *
 * ── ★ v27 — 화면의 가로축이 벽시계가 됐다 (`docs/55` · 절대 규칙 73) ──
 *
 * **이 응답은 한 필드도 안 바뀌었다.** 시각은 여전히 없고 `generatedAt` 하나뿐이며,
 * 그것은 **응답의 나이**다. 눈금을 만드는 것은 **화면**이고 **기기의 시계**로 만든다 —
 * 서버가 「몇 시에 무엇」을 말하기 시작하면 그 순간 절대 규칙 73 이 지키던 셋 중 하나가
 * 무너진다.
 *
 * 더해진 것은 **순수 함수 둘**이다 — `lineupSpans`(자리와 폭)와 `elapsedWallSec`
 * (「지금」 선 왼쪽으로 뻗는 길이). `lineupStartOffsets` 는 **그 위에서 다시 구현됐고**,
 * 그래서 산술은 여전히 **한 곳**이다(절대 규칙 57).
 */
import { midrollCuePoints, effectiveDurationSec } from './ad.js';
/**
 * `startFromProductId` 회전 — 특허 ④ 의 「제2 플레이리스트」 구성.
 *
 * 해당 항목이 목록에 있으면 그 위치부터 끝까지를 앞으로 당기고 앞부분을 뒤에 붙인다.
 * 없으면 **무시하고 원본 순서를 그대로** 돌려준다(에러가 아니다 — `docs/03`).
 *
 * ── 왜 계약에 있는가 ────────────────────────────────────────
 *
 * **v25 가 서버에서 여기로 옮겼다.** 회전이 두 곳에서 필요해졌기 때문이다 —
 * 서버는 「그 채널을 켜면 무엇이 나오는가」로, 앱은 **「지금 보고 있는 채널의 행을 재생
 * 중인 항목 기준으로」**(`applyCurrentRow`). **구현이 둘이 되면 편성표와 재생이 갈리고**,
 * 그 어긋남은 절대 규칙 68 이 경고하는 그 모양이다.
 *
 * `packages/bloom-server/src/playlist/rotate.ts` 는 **여기를 다시 내보내기만 한다** —
 * 배포된 import 경로를 깨뜨리지 않으려는 것이고, 구현은 이 함수 하나다.
 */
export function rotateToProduct(items, startFromProductId) {
    if (!startFromProductId)
        return [...items];
    const index = items.findIndex((item) => item.productId === startFromProductId);
    if (index <= 0)
        return [...items];
    return [...items.slice(index), ...items.slice(0, index)];
}
/**
 * v39. **재생 순서의 유일한 정의** — 편성 · 회전 · 편성 밖 회차의 시리즈 꼬리 (절대 규칙 48 · 104 · `docs/81` §2).
 *
 * ```
 * 기준이 없거나 편성에도 시리즈 회차에도 없다 → 편성 그대로            (처음 보는 채널 · 낡은 지점)
 * 기준이 편성 안                              → rotateToProduct(편성, 기준) (v38 까지와 같다)
 * 기준이 편성 밖                              → [꼬리…, …rotateToProduct(편성, 합류)]
 *   꼬리 = 시리즈 회차에서 기준부터 순서대로, 처음 만나는 편성 회차 직전까지
 *   합류 = 그 편성 회차 — 없으면 편성의 처음부터
 * ```
 *
 * ── 왜 「떠나온 자리」로 돌아가지 않는가 ──────────────────────
 *
 * 서버는 시청자가 어디서 떠났는지 모른다. 그것을 저장하면 재진입(이어보기 행으로 짜는 목록)과 세션 안 선택이
 * **두 갈래**가 되고, 그 증상은 언제나 「이어보기가 이상하다」다(`docs/81` D1). 이 함수는 입력이 같으면 언제나
 * 같은 목록을 낸다 — 재생(`/playlist`)과 편성표(`/lineup`)가 이것 하나를 부르는 이유다.
 *
 * ── ★ 결과 안에서 `productId` 는 유일하다 ────────────────────
 *
 * 꼬리는 정의상 편성 회차를 담지 않는다(만나면 거기서 멈춘다). 이 성질에 기대는 자리가 셋이다 — 앱의
 * `toProductRows`(`current` 는 많아야 하나) · `applyCurrentRow`(회전) · 리시버의 인덱스.
 *
 * @param scheduled 편성 순서의 **재생 가능한** 회차
 * @param anchorProductId 고른 회차(`startFromProductId`) 또는 이어보기 행의 회차
 * @param seriesEpisodes 기준 회차가 **편성 밖일 때만** 그 시리즈의 공개·재생 가능한 회차(시즌 → 회차 순).
 *   편성 안이거나 모르면 빈 배열이다 — 그때는 회전만 한다.
 */
export function composeLineup(scheduled, anchorProductId, seriesEpisodes) {
    if (!anchorProductId)
        return [...scheduled];
    if (scheduled.some((item) => item.productId === anchorProductId)) {
        return rotateToProduct(scheduled, anchorProductId);
    }
    const start = seriesEpisodes.findIndex((item) => item.productId === anchorProductId);
    if (start < 0)
        return [...scheduled];
    const scheduledIds = new Set(scheduled.map((item) => item.productId));
    const tail = [];
    let join;
    for (const item of seriesEpisodes.slice(start)) {
        if (scheduledIds.has(item.productId)) {
            join = item.productId;
            break;
        }
        tail.push(item);
    }
    return [...tail, ...rotateToProduct(scheduled, join)];
}
/**
 * `PlaylistItem` → 카드. **여기서 재생 필드가 떨어져 나간다** (절대 규칙 68 · 105).
 *
 * `videoUrl`·`deepLink`·`skip`·`cues` 를 옮기지 않는 것이 이 함수의 존재 이유다 — 스프레드(`{ ...item }`)로 쓰면
 * 그 넷이 조용히 따라 들어오고, **화면은 아무 차이 없이 잘 돌아간다.** 필드를 하나씩 적는 것이 그 사고를 막는
 * 유일한 방법이다.
 *
 * ★ **v39 가 서버에서 여기로 옮겼다** — 편성표(서버) · 카탈로그(서버) · 보고 있는 채널의 행을 리시버의 목록으로
 * 세우는 자리(앱의 `applyCurrentRow`) 셋이 쓴다. 두 벌이 되면 한쪽에만 재생 필드가 샌다. v25 가 `rotateToProduct`
 * 를 옮긴 것과 같은 이유다.
 */
export function toLineupItem(item) {
    return {
        productId: item.productId,
        title: item.title,
        thumbnailUrl: item.thumbnailUrl,
        // 콘텐츠 축 그대로다 (절대 규칙 57·61). 벽시계로 바꾸는 것은 화면이다.
        durationSec: item.durationSec,
        price: item.price,
        currency: item.currency,
        meta: { category: item.meta.category },
        content: item.content,
    };
}
/**
 * 채널 하나의 카드별 「지금부터 몇 초 뒤에 시작하는가」. **벽시계 축이다** (절대 규칙 57).
 *
 * 시청자에게 말하는 소요이므로 콘텐츠 축으로 세면 안 된다 — 광고가 붙는 드라마 채널에서
 * **회차마다 어긋나고**, 셋째 카드쯤이면 오차가 십수 분이다.
 *
 * ── ★ 첫 장만 남은 길이이고 나머지는 전체 길이다 ──────────────
 *
 * 이어보기 지점이 있으면 그 회차는 이미 그만큼 지나갔다. 전체 길이로 세면 **그 채널의
 * 모든 카드가 통째로 밀린다** — 그리고 그 실수는 **이력이 없는 계정에서는 정확해 보인다.**
 *
 * ── ★★ 지나온 미드롤을 손으로 세지 않는다 ────────────────────
 *
 * `midrollCuePoints` 를 그대로 걸러 쓴다. 손으로 계산하면 **꼬리 컷 버림(절대 규칙 52)이
 * 두 벌**이 되고, 그 어긋남은 「광고가 하나 더/덜 있는 것처럼 계산된다」로 **아무 오류
 * 없이** 나타난다.
 *
 * `policy` 가 `null` 이면 — 광고 없는 채널이거나 **시청자가 광고를 껐을 때** — 벽시계와
 * 콘텐츠 축이 같아진다. **끈 상태에서 광고를 더하면 없는 시간을 말하는 화면이 된다**
 * (`ProductListSheet` 가 `+광고 n분` 배지에 대해 이미 그렇게 한다).
 *
 * **이 산술을 앱·콘솔·서버 어디서도 다시 쓰지 않는다** — 절대 규칙 57 의 「한 곳」이
 * 이 함수다.
 *
 * @returns `items` 와 같은 길이. `[0]` 은 언제나 0 이다 (「지금」).
 */
export function lineupStartOffsets(policy, items, resumePositionSec) {
    /*
     * ★ v27 — **`lineupSpans` 위에서 다시 구현됐다.** 산술이 둘이 되면 「약 n분 후」와
     * 상자의 자리가 갈리는데, 둘은 **같은 화면의 같은 상자**에 붙어 있어 그 어긋남이
     * 눈으로는 안 갈린다(하나는 글자, 하나는 픽셀이다).
     *
     * 첫 장의 `startSec` 은 **음수**(이미 본 만큼)라 그것을 그대로 쓰면 안 된다 —
     * 이 함수의 계약은 「`[0]` 은 언제나 0」이다. 그래서 첫 장만 0 으로 눌러 준다.
     */
    const spans = lineupSpans(policy, items, resumePositionSec, {
        untilSec: 0, // 한 바퀴만 — 반복은 이 함수의 계약이 아니다
        maxSpans: items.length,
    });
    return spans.map((span, i) => (i === 0 ? 0 : span.startSec));
}
/** 창을 채울 때까지 돌리는 기본값. 화면의 `EPG_FILL_MIN` 과 같은 뜻이다 */
const LINEUP_FILL_SEC = 3600;
/** 한 행의 상자 상한. **길이가 0 인 편성에서 무한 반복을 막는 마지막 방어선이다** */
const LINEUP_MAX_SPANS = 32;
/**
 * 채널 하나의 시간축 배치 (v27). **절대 규칙 57 의 「한 곳」이 이 함수다.**
 *
 * ── ★ `endSec` 은 다음 `startSec` 과 **정확히 같다** ────────────
 *
 * 폭을 따로(`effectiveDurationSec`) 계산하면 반올림이 쌓여 **상자 사이에 1px 씩 틈이나
 * 겹침**이 생긴다. 폭은 언제나 `endSec - startSec` 이고, **자리와 폭이 같은 수에서
 * 나온다.**
 *
 * ── ★★ 창을 채울 때까지 돌린다 ────────────────────────────────
 *
 * 리시버는 마지막 회차 뒤에 **처음으로 되돈다**(`(index + 1) % length`). 한 바퀴만
 * 그리면 창의 절반이 빈 채로 남고, 그 빈 자리는 「이 채널은 곧 끝난다」로 읽히는데
 * **참이 아니다.** 두 번째 바퀴부터는 이어보기가 없으므로 **첫 장도 전체 길이**다.
 *
 * `untilSec: 0` 을 주면 **한 바퀴만** 낸다 — `lineupStartOffsets` 가 그렇게 쓴다.
 *
 * @returns `items` 가 비면 빈 배열. 그 밖에는 최소 한 바퀴, 최대 `maxSpans` 개.
 */
export function lineupSpans(policy, items, resumePositionSec, options = {}) {
    if (items.length === 0)
        return [];
    const untilSec = options.untilSec ?? LINEUP_FILL_SEC;
    const maxSpans = Math.max(1, options.maxSpans ?? LINEUP_MAX_SPANS);
    const breakSec = policy?.betweenEpisodesSec ?? 0;
    const spans = [];
    // 첫 장은 이미 지나간 만큼 왼쪽에서 시작한다. 이어보기가 없으면 0 이라 「지금」 선이다.
    //
    // ★ `-0` 을 만들지 않는다. 화면에서는 0 과 똑같이 그려지지만 `Object.is(-0, 0)` 이
    //   거짓이라 **테스트만 빨개진다** — 실제로 이 테스트가 처음 돌 때 그렇게 걸렸다.
    const elapsed = elapsedWallSec(policy, items[0]?.durationSec ?? 0, resumePositionSec);
    let at = elapsed === 0 ? 0 : -elapsed;
    for (let n = 0; n < maxSpans; n += 1) {
        const index = n % items.length;
        const item = items[index];
        if (item === undefined)
            break;
        /*
         * 첫 바퀴의 첫 장만 「전체 길이」다 — 이미 `at` 이 음수로 물러나 있으므로 그 폭
         * 안에 이미 본 구간이 함께 들어간다. 되돌아온 바퀴의 같은 항목은 **처음부터**다.
         */
        const wall = effectiveDurationSec(policy, item.durationSec);
        const end = at + wall;
        spans.push({ index, cycle: Math.floor(n / items.length), startSec: at, endSec: end, breakSec });
        at = end;
        // 한 바퀴는 무조건 채우고, 그 뒤로는 창을 덮을 때까지만.
        if (n + 1 >= items.length && at >= untilSec)
            break;
    }
    return spans;
}
/**
 * 그 회차에서 **이미 지나간 벽시계 초** (v27). 「지금」 선 왼쪽으로 뻗는 길이다.
 *
 * ── ★ 뺄셈으로 정의한다 ────────────────────────────────────────
 *
 * `effectiveDurationSec - remainingWallSec` 이다. 따로 세면 **지나온 미드롤을 손으로
 * 세게 되고**(절대 규칙 52 가 두 벌이 된다), 그 어긋남은 **왼쪽 꼬리가 몇 픽셀 길거나
 * 짧다**로만 나타나 **아무도 신고하지 않는다.** 뺄셈이면 **원리적으로 어긋날 수 없다.**
 *
 * `resumePositionSec` 이 `null`·0 이면 0 이다 — 홈쇼핑·비로그인·처음 보는 채널이 전부
 * 그 갈래이고, 그래서 화면에 `kind` 분기가 없다.
 */
export function elapsedWallSec(policy, durationSec, resumePositionSec) {
    return (effectiveDurationSec(policy, durationSec) -
        remainingWallSec(policy, durationSec, resumePositionSec));
}
/**
 * 지금 보고 있는(또는 두고 온) 회차의 **남은 벽시계 초**.
 *
 * `resumePositionSec` 이 `null`·0 이면 `effectiveDurationSec` 전체와 같아진다 —
 * 분기가 아니라 `?? 0` 하나로 같은 식이 된다.
 */
function remainingWallSec(policy, durationSec, resumePositionSec) {
    const at = Math.max(0, Math.min(resumePositionSec ?? 0, durationSec));
    const remainingContent = durationSec - at;
    if (policy === null)
        return remainingContent;
    // ★ 지나온 컷은 빼고 **남은 컷만** 센다. midrollCuePoints 를 다시 구현하지 않는다.
    const remainingMidrolls = midrollCuePoints(policy, durationSec).filter((p) => p > at).length;
    return remainingContent + remainingMidrolls * policy.midrollDurationSec + policy.betweenEpisodesSec;
}
//# sourceMappingURL=lineup.js.map